import Annotation from "./Annotation";
import Point from "../../geom/Point";
import Location from "../../gis/Location";
import MapElementStyle from "./MapElementStyle";
import LangUtils from "../../utils/LangUtils";
import * as tinycolor from 'tinycolor2';
import MapObject from "../../model/MapObject";
import TokenResolver from "@/token/TokenResolver";
import MapObjectShape from "./MapObjectShape";

export default class MapObjectAnnotation extends Annotation {
    constructor(mapObject, normalStyle, selectedStyle, projection, language) {
        super(mapObject.uid ? mapObject.uid : mapObject.id, mapObject.hidden);
        this.mapObject = mapObject;
        this.normalStyle = normalStyle;
        this.selectedStyle = selectedStyle;
        this.statusVisibleStyle = null;
        this.projection = projection;
        this.language = language;

        if (!normalStyle) {
            this.normalStyle = {
                id: this._getStyleIdForPoiType()
            };
        }
    }

    getName() {
        if (this.mapObject.name) {
            return LangUtils.valueForLanguage(this.mapObject.name, this.language);
        } else {
            return "";
        }
    }

    getSummary() {
        if (this.mapObject.summary) {
			var summary = LangUtils.valueForLanguage(this.mapObject.summary, this.language);
			if (summary) {
				summary = TokenResolver.resolveTextTokens(this.mapObject, summary);
			}
            return summary;
        } else {
            return null;
        }
    }

    getDescription() {
        if (this.mapObject._htmlDescription) {
            return LangUtils.valueForLanguage(this.mapObject._htmlDescription, this.language);
        } else if (this.mapObject.description) {
            return LangUtils.valueForLanguage(this.mapObject.description, this.language);
        } else {
            return null;
        }
    }

    getPlaceInformation() {
        return this.mapObject.placeInformation;
    }

    getTrailInformation() {
		return this.mapObject.trailInformation;
	}

    getTabs() {
        return this.mapObject.tabs;
    }

    getType() {
        return this.mapObject.type;
    }

    getIconFileName() {
        return this.mapObject.iconFileName
    }

    isHideable() {
        return this.mapObject.hideable;
    }

    getLocation() {
        if (this.mapObject.location) {
            return new Location(this.mapObject.location.latitude, this.mapObject.location.longitude);
        } else if (this.mapObject.latLong) {
            return new Location(this.mapObject.latLong.latitude, this.mapObject.latLong.longitude);
        } else if (this.mapObject.origin) {
            return this.projection.pointToLocation(new Point(this.mapObject.origin.x, this.mapObject.origin.y));
        }
        return null;
    }

    getWorldLocation(projectionProxy) {
        if (this.mapObject.location || this.mapObject.latLong) {
            return projectionProxy.toWorldLocation(this.getLocation());
        } else if (this.mapObject.origin) {
            return projectionProxy.toWorldLocation(new Point(this.mapObject.origin.x, this.mapObject.origin.y));
        }
        return null;
    }

    getMapElementStyle(urlToAsset) {
        if (this.normalStyle) {
            if (this.normalStyle.imageFileName) {
                var filename = this.normalStyle.imageFileName.replace(".png", "@2x.png").replace(".jpg", "@2x.jpg");
                const url = urlToAsset + "/" + filename;
                return new MapElementStyle(this.normalStyle.id, url);
            } else {
                this.normalStyle.id = MapObject.PoiType.NORMAL;
                return new MapElementStyle(MapObject.PoiType.NORMAL, null);
            }
        } else {
            return new MapElementStyle(MapObject.PoiType.NORMAL, null);
        }
    }

    _getStyleIdForPoiType() {
        return this.mapObject.poiType ? this.mapObject.poiType : MapObject.PoiType.NORMAL;
    }

    _mustDisplayStatusLabel() {
        if (this.mapObject.children && this.mapObject.children.length > 0) {
            for (const child of this.mapObject.children) {
                if (child instanceof MapObjectShape) {
                    return false;
                }
            }
            return true;
        } else {
            return true;
        }
    }

    toGeoJSONFeature(baseOSMZoom, projectionProxy) {
        baseOSMZoom;
        projectionProxy;

        var worldLocation;
        if (this.mapObject.location || this.mapObject.latLong) {
            worldLocation = projectionProxy.toWorldLocation(this.getLocation());
        } else if (this.mapObject.origin) {
            worldLocation = projectionProxy.toWorldLocation(new Point(this.mapObject.origin.x, this.mapObject.origin.y));
        }

        const properties = {
            id: this.getId(),
            type: "annotation",
            interactive: this.isInteractive(),
            scalable: this.normalStyle.scalable ? true : false,
            hidden: this.isHidden()
        };

        if (this.normalStyle) {
            properties.styleId = this.normalStyle.id;
        }

        if (this.statusVisibleStyle) {
            if (this._mustDisplayStatusLabel()) {
                properties.statusVisibleLabel = LangUtils.valueForLanguage(this.statusVisibleStyle.label, this.language);
            }

            if (tinycolor(this.statusVisibleStyle.strokeColor).getBrightness() > 150) {
                properties.statusVisibleLabelColor = "#000000";
            } else {
                properties.statusVisibleLabelColor = "#ffffff";
            }

            properties.statusVisibleLabelHaloColor = this.statusVisibleStyle.strokeColor;
        }

        this._addIconScaleProperties(properties, this.normalStyle.scalable, baseOSMZoom, this.normalStyle, projectionProxy);

        return {
            type: "Feature",
            properties: properties,
            geometry: {
                type: "Point",
                coordinates: [worldLocation ? worldLocation.longitude : 0, worldLocation ? worldLocation.latitude : 0],
            }
        }
    }

    _addIconScaleProperties(feature, scalable, baseOSMZoom, style, projectionProxy) {
        const originOffset = this._extractIconOriginOffsetFromStyle(style);

		if (scalable) {
            var step = 1;
            for (var zoom = baseOSMZoom - 1; zoom >= 0; zoom --) {
                var worldZoomStop = projectionProxy.toWorldZoom(zoom);
                feature[this._zoomStopProperty(worldZoomStop, "iconScale")] = 0.5 / step;
                feature[this._zoomStopProperty(worldZoomStop, "iconOffsetArray")] = [ -originOffset.x, -originOffset.y ];
                
                step = step + 1;
            }
    
            step = 2;
            for (zoom = baseOSMZoom; zoom <= 30; zoom ++) {
                worldZoomStop = projectionProxy.toWorldZoom(zoom);
                feature[this._zoomStopProperty(worldZoomStop, "iconScale")] = step * 0.5;
                feature[this._zoomStopProperty(worldZoomStop, "iconOffsetArray")] = [ -originOffset.x, -originOffset.y ];

                step = step + 1;
            }
        } else {
            const worldZoomStop = projectionProxy.toWorldZoom(0.0);
            feature[this._zoomStopProperty(worldZoomStop, "iconScale")] = 0.5;
            feature[this._zoomStopProperty(worldZoomStop, "iconOffsetArray")] = [ -originOffset.x * 2, -originOffset.y * 2 ];
        }
    }

    _extractIconOriginOffsetFromStyle(style) {
        var iconOriginOffset;

        if (style && style.originOffset) {
			const originalOffset = style.originOffset;
			const size = style.size;
			if (size != null) {
				const newOffset = new Point(originalOffset.x - (size.width / 2.0), originalOffset.y - (size.height / 2.0));
				iconOriginOffset = newOffset;
			} else {
				iconOriginOffset = new Point(0, 0);
			}
		} else {
			iconOriginOffset = new Point(0, 0);
		}

        return iconOriginOffset;
    }
}